import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { getAiHealthReport, quickFixArticle, type LangFilter } from "@/lib/ai-health.functions";
import { quickFixCop31Note } from "@/lib/cop31-note-health.functions";
import { AdminLangTabs } from "@/components/AdminLangTabs";
import { cop31NoteSlug } from "@/lib/cop31-notes";

export const Route = createFileRoute("/admin/ai-health")({
  component: AiHealthPage,
});

function Scorecard({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="border border-border rounded p-5 bg-paper">
      <div className="text-[10px] uppercase tracking-[0.22em] text-ink/60 font-bold">
        {label}
      </div>
      <div className="mt-3 font-serif text-5xl leading-none">{value}%</div>
      {hint && <div className="mt-2 text-xs text-ink/60 leading-relaxed">{hint}</div>}
      <div className="mt-4 h-1.5 bg-ink/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-clay rounded-full"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  );
}

function Check({ ok }: { ok: boolean }) {
  return ok ? (
    <span className="text-emerald-600 font-bold" aria-label="var">✓</span>
  ) : (
    <span className="text-rose-500 font-bold" aria-label="yok">×</span>
  );
}

function AiHealthPage() {
  const load = useServerFn(getAiHealthReport);
  const [lang, setLang] = useState<LangFilter>("all");
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "ai-health", lang],
    queryFn: () => load({ data: { lang } }),
  });

  if (isLoading) {
    return <div className="text-sm text-ink/60">Sağlık raporu hesaplanıyor…</div>;
  }
  if (isError || !data) {
    return (
      <div className="text-sm text-rose-600">
        Yüklenemedi: {error instanceof Error ? error.message : "Bilinmeyen hata"}
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl">AI Health & Action</h1>
          <p className="text-sm text-ink/60 mt-1">
            İçeriğin yapay zekâ tarafından okunabilirliği ve bilgi ağı derinliği.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AdminLangTabs value={lang} onChange={setLang} />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-[11px] uppercase tracking-[0.2em] border border-ink/25 px-3 py-1.5 hover:border-clay hover:text-clay disabled:opacity-50"
          >
            {isFetching ? "Yenileniyor…" : "Yenile"}
          </button>
        </div>
      </header>

      {/* 1. Scorecards */}
      <section className="grid gap-4 md:grid-cols-3">
        <Scorecard
          label="AI Readiness (Genel)"
          value={data.overall}
          hint="Tüm makalelerin 5 kriter üzerinden ortalaması."
        />
        <Scorecard
          label="Concept Coverage"
          value={data.conceptCoverage}
          hint="Sözlükteki özgün kavramların makalelerde kapsanma oranı."
        />
        <Scorecard
          label="Resource Depth"
          value={data.resourceDepth}
          hint="Makalelerin rapor ve dış kaynaklarla bağlanma derinliği."
        />
      </section>

      {/* Quick Fix — lowest-scoring posts */}
      <QuickFixPanel rows={data.rows} lang={lang} onDone={() => refetch()} />



      {/* 2. Content Health Table */}
      <section>
        <h2 className="font-serif text-xl mb-3">AI Content Health</h2>
        <div className="overflow-x-auto border border-border rounded">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-ink/[0.03] text-[10px] uppercase tracking-[0.18em] text-ink/60">
              <tr className="text-left">
                <th className="px-3 py-2 font-bold">Yazı</th>
                <th className="px-2 py-2 font-bold text-center">5 sn Cevap</th>
                <th className="px-2 py-2 font-bold text-center">Çıkarımlar</th>
                <th className="px-2 py-2 font-bold text-center">Kaynak/Atıf</th>
                <th className="px-2 py-2 font-bold text-center">Kavramlar</th>
                <th className="px-2 py-2 font-bold text-center">Rapor</th>
                <th className="px-3 py-2 font-bold text-right">Skor</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={`${r.kind}-${r.slug}`} className="border-t border-border">
                  <td className="px-3 py-2">
                    {r.kind === "cop31-note" ? (
                      <span className="flex items-center gap-2">
                        <span className="text-[9px] uppercase tracking-[0.16em] border border-clay/40 text-clay px-1.5 py-0.5 rounded">
                          COP31
                        </span>
                        <Link
                          to="/cop31/notlar/$slug"
                          params={{ slug: cop31NoteSlug(r.slug, "tr") }}
                          className="hover:text-clay"
                        >
                          {r.title}
                        </Link>
                      </span>
                    ) : (
                      <Link
                        to="/yazilar/$slug"
                        params={{ slug: r.slug }}
                        className="hover:text-clay"
                      >
                        {r.title}
                      </Link>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center"><Check ok={r.hasAnswer} /></td>
                  <td className="px-2 py-2 text-center"><Check ok={r.hasTakeaways} /></td>
                  <td className="px-2 py-2 text-center"><Check ok={r.hasSources} /></td>
                  <td className="px-2 py-2 text-center"><Check ok={r.hasConcepts} /></td>
                  <td className="px-2 py-2 text-center"><Check ok={r.hasReportLink} /></td>
                  <td
                    className={
                      "px-3 py-2 text-right font-bold tabular-nums " +
                      (r.score >= 80
                        ? "text-emerald-600"
                        : r.score >= 60
                          ? "text-amber-600"
                          : "text-rose-600")
                    }
                  >
                    {r.score}%
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-ink/50">
                    Yayında yazı yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 3. Suggestions */}
      <section>
        <h2 className="font-serif text-xl mb-3">Bugünün 5 Aksiyonu</h2>
        {data.suggestions.length === 0 ? (
          <div className="text-sm text-ink/60 border border-border rounded p-4">
            Şu an için önerilen bir aksiyon yok. Her şey yerinde görünüyor.
          </div>
        ) : (
          <ol className="space-y-2 list-none pl-0">
            {data.suggestions.map((s, i) => (
              <li
                key={i}
                className="relative pl-10 pr-4 py-3 border border-border rounded bg-paper text-sm leading-relaxed"
              >
                <span className="absolute left-3 top-3 font-serif italic text-clay">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s}
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* 4. Knowledge Graph Stats */}
      <section>
        <h2 className="font-serif text-xl mb-3">Bilgi Ağı</h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { k: "Makale", v: data.stats.posts },
            { k: "Kavram", v: data.stats.concepts },
            { k: "Rapor", v: data.stats.reports },
            { k: "Kitap", v: data.stats.books },
            { k: "Konuşma", v: data.stats.talks },
            { k: "Podcast", v: data.stats.podcast },
          ].map((s) => (
            <div key={s.k} className="border border-border rounded p-4 text-center bg-paper">
              <div className="font-serif text-3xl">{s.v}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink/60 mt-1">
                {s.k}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ---------------- Quick Fix Panel ----------------
type HealthRow = {
  id: string;
  slug: string;
  kind?: "post" | "cop31-note";
  title: string;
  score: number;
  hasAnswer: boolean;
  hasTakeaways: boolean;
  hasSources: boolean;
  hasConcepts: boolean;
  hasReportLink: boolean;
};

function QuickFixPanel({ rows, lang, onDone }: { rows: HealthRow[]; lang: LangFilter; onDone: () => void }) {
  const quickFixes = rows.filter((r) => r.score < 100).slice(0, 3);
  const fix = useServerFn(quickFixArticle);
  const fixNote = useServerFn(quickFixCop31Note);
  const qc = useQueryClient();
  const [runningId, setRunningId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (row: HealthRow) =>
      row.kind === "cop31-note"
        ? fixNote({ data: { slug: row.slug } })
        : fix({ data: { id: row.id, lang } }),

    onMutate: (row) => setRunningId(row.id),
    onSettled: () => setRunningId(null),
    onSuccess: (report) => {
      const fixed = report.fixed.length;
      const errs = report.errors.length;
      if (fixed > 0 && errs === 0) {
        toast.success(`Tamir tamam: ${fixed} adım`, {
          description: report.fixed.join(" • "),
        });
      } else if (fixed > 0 && errs > 0) {
        toast.warning(`Kısmen tamir edildi (${fixed} tamam, ${errs} hata)`, {
          description: [...report.fixed, ...report.errors.map((e) => `⚠ ${e.step}: ${e.message}`)].join(" • "),
        });
      } else if (errs > 0) {
        toast.error("Tamir başarısız", {
          description: report.errors.map((e) => `${e.step}: ${e.message}`).join(" • "),
        });
      } else {
        toast(`Değişiklik yapılmadı`, {
          description: report.skipped.join(" • ") || "Yapılacak bir şey yoktu.",
        });
      }
      qc.invalidateQueries({ queryKey: ["admin", "ai-health"] });
      onDone();
    },
    onError: (e) => {
      toast.error("Tamir başarısız", {
        description: e instanceof Error ? e.message : String(e),
      });
    },
  });

  if (quickFixes.length === 0) return null;

  const missingLabels: Record<string, string> = {
    hasAnswer: "5 sn cevap",
    hasTakeaways: "Çıkarımlar",
    hasSources: "Kaynak",
    hasConcepts: "Kavramlar",
    hasReportLink: "Rapor",
  };

  return (
    <section className="border-l-4 border-clay bg-clay/5 rounded-r p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-serif text-xl">
          <span className="text-clay">⚡</span> Quick Fix — Hızlı Tamir
        </h2>
        <span className="text-[10px] uppercase tracking-[0.2em] text-ink/60">
          En düşük skorlu {quickFixes.length} yazı
        </span>
      </div>
      <ul className="space-y-2">
        {quickFixes.map((r) => {
          const missing = (
            [
              ["hasAnswer", r.hasAnswer],
              ["hasTakeaways", r.hasTakeaways],
              ["hasSources", r.hasSources],
              ["hasConcepts", r.hasConcepts],
              ["hasReportLink", r.hasReportLink],
            ] as [string, boolean][]
          )
            .filter(([, ok]) => !ok)
            .map(([k]) => missingLabels[k]);
          const busy = runningId === r.id;
          return (
            <li
              key={r.id}
              className="flex items-start justify-between gap-3 bg-paper border border-border rounded p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={
                      "text-xs font-bold tabular-nums px-2 py-0.5 rounded " +
                      (r.score >= 60
                        ? "bg-amber-100 text-amber-700"
                        : "bg-rose-100 text-rose-700")
                    }
                  >
                    {r.score}%
                  </span>
                  {r.kind === "cop31-note" && (
                    <span className="text-[9px] uppercase tracking-[0.16em] border border-clay/40 text-clay px-1.5 py-0.5 rounded">
                      COP31
                    </span>
                  )}
                  <Link
                    to={r.kind === "cop31-note" ? "/cop31-guide/notes/$slug" : "/yazilar/$slug"}
                    params={{ slug: r.kind === "cop31-note" ? cop31NoteSlug(r.slug, "tr") : r.slug }}
                    className="font-medium truncate hover:text-clay"
                  >
                    {r.title}
                  </Link>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {missing.map((m) => (
                    <span
                      key={m}
                      className="text-[10px] uppercase tracking-[0.14em] border border-rose-300 text-rose-600 px-1.5 py-0.5 rounded"
                    >
                      eksik: {m}
                    </span>
                  ))}
                </div>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1.5">
                <button
                  onClick={() => mutation.mutate(r)}
                  disabled={busy || mutation.isPending}
                  className="text-[11px] uppercase tracking-[0.2em] border border-clay text-clay px-3 py-1.5 hover:bg-clay hover:text-paper disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? "Tamir ediliyor…" : "⚡ Tamir Et"}
                </button>
                {r.kind === "cop31-note" ? (
                  <Link
                    to="/cop31/notlar/$slug"
                    params={{ slug: cop31NoteSlug(r.slug, "tr") }}
                    className="text-[10px] uppercase tracking-[0.18em] text-ink/50 hover:text-clay"
                  >
                    Notu görüntüle →
                  </Link>
                ) : (
                  <Link
                    to="/admin/$id"
                    params={{ id: r.id }}
                    className="text-[10px] uppercase tracking-[0.18em] text-ink/50 hover:text-clay"
                  >
                    Elle düzenle →
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[11px] text-ink/50 leading-relaxed">
        "Tamir Et" makaledeki tüm eksikleri sırayla giderir: 5 sn cevap, temel
        çıkarımlar, dış kaynaklar, rapor bağlantısı ve sözlük kavramı.
        Değişiklikler yayına doğrudan yansır.
      </p>
    </section>
  );
}

